import { ReactNode, useEffect, useRef, useState } from "react";
import { Check, LayoutGrid, List } from "lucide-react";
import clsx from "clsx";

export function useListingView(key: string, initial: "grid" | "list" = "grid") {
  const [view, setView] = useState<"grid" | "list">(() => {
    try {
      const saved = localStorage.getItem(key);
      return saved === "list" || saved === "grid" ? saved : initial;
    } catch {
      return initial;
    }
  });
  return [
    view,
    (next: "grid" | "list") => {
      setView(next);
      try {
        localStorage.setItem(key, next);
      } catch {
        /* The view still works without browser storage. */
      }
    },
  ] as const;
}

export function ListingViewToggle({
  value,
  onChange,
}: {
  value: "grid" | "list";
  onChange: (value: "grid" | "list") => void;
}) {
  return (
    <div
      className="inline-flex shrink-0 gap-1 rounded-xl border border-border bg-surface-alt p-1"
      role="group"
      aria-label="Display layout"
    >
      {(
        [
          { key: "grid", label: "Grid view", Icon: LayoutGrid },
          { key: "list", label: "List view", Icon: List },
        ] as const
      ).map(({ key, label, Icon }) => (
        <button
          key={key}
          type="button"
          aria-label={label}
          title={label}
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          className={clsx(
            "flex h-8 w-9 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            value === key
              ? "bg-surface text-primary shadow-sm"
              : "text-text-muted hover:text-text-primary",
          )}
        >
          <Icon size={17} />
        </button>
      ))}
    </div>
  );
}

export function EditorSteps({
  steps,
  current,
  onChange,
}: {
  steps: { label: string; description: string; done?: boolean }[];
  current: number;
  onChange: (step: number) => void;
}) {
  const container = useRef<HTMLElement>(null);
  const previous = useRef(current);
  useEffect(() => {
    if (previous.current !== current)
      container.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    previous.current = current;
  }, [current]);
  return (
    <nav
      ref={container}
      aria-label="Creation steps"
      className="mb-7 flex scroll-mt-28 gap-1 overflow-x-auto rounded-2xl border border-border bg-surface p-2"
    >
      {steps.map((step, index) => (
        <button
          type="button"
          key={step.label}
          onClick={() => onChange(index)}
          aria-current={current === index ? "step" : undefined}
          className={clsx(
            "flex min-w-[150px] flex-1 items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-w-0",
            current === index ? "bg-primary-soft" : "hover:bg-surface-alt",
          )}
        >
          <span
            className={clsx(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-semibold",
              current === index
                ? "bg-gold-gradient text-on-primary"
                : step.done
                  ? "bg-primary-soft text-primary"
                  : "bg-surface-alt text-text-muted",
            )}
          >
            {step.done && current !== index ? (
              <Check size={17} />
            ) : (
              `0${index + 1}`
            )}
          </span>
          <span className="min-w-0">
            <span
              className={clsx(
                "block text-sm font-semibold",
                current === index ? "text-text-primary" : "text-text-secondary",
              )}
            >
              {step.label}
            </span>
            <span className="mt-0.5 hidden text-xs text-text-muted xl:block">
              {step.description}
            </span>
          </span>
        </button>
      ))}
    </nav>
  );
}

export function ListingSummary({
  items,
}: {
  items: { label: string; value: ReactNode; detail: string; icon: ReactNode }[];
}) {
  return (
    <div className="mb-7 grid grid-cols-2 overflow-hidden rounded-2xl border border-border bg-surface lg:flex">
      {items.map((item) => (
        <div
          key={item.label}
          className="min-w-0 flex-1 border-b border-r border-border p-5 last:border-r-0 lg:border-b-0"
        >
          <div className="mb-3 flex items-center justify-between gap-2 text-sm text-text-secondary">
            {item.label}
            <span className="text-primary">{item.icon}</span>
          </div>
          <div className="text-3xl font-semibold tracking-tight text-text-primary tabular-nums">
            {item.value}
          </div>
          <div className="mt-1.5 text-xs text-text-muted">{item.detail}</div>
        </div>
      ))}
    </div>
  );
}
